import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import './index.module.css';

const DEFAULT_PRODUCTS = [
  {
    name: 'Signature',
    href: '/browse-styles/signature',
    image: '/-/media/Project/VicRoads/Plates/home-content/best-sellers-signature-2.jpg',
    imageAlt: 'A car with a Signature style plate'
  },
  {
    name: 'Euro',
    href: '/browse-styles/euro',
    image: '/-/media/Project/VicRoads/Plates/home-content/best-sellers-euro-2.jpg',
    imageAlt: 'A car with a Euro style plate'
  },
  {
    name: 'Style',
    href: '/browse-styles/style',
    image: '/-/media/Project/VicRoads/Plates/home-content/best-sellers-style-2.jpg',
    imageAlt: 'Examples of Style plates'
  },
  {
    name: 'AFL Premium',
    href: '/browse-styles/afl-premium-teams',
    image: '/-/media/Project/VicRoads/Plates/home-content/best-sellers-afl-premium-2.jpg',
    imageAlt: 'A car with an AFL Premium style plate'
  },
  {
    name: 'DC Comics',
    href: '/browse-styles/dc-comics',
    image: '/-/media/Project/VicRoads/Plates/home-content/best-sellers-dc-2.jpg',
    imageAlt: 'A car with a DC Comics style plate'
  },
  {
    name: 'Deluxe',
    href: '/browse-styles/deluxe',
    image: '/-/media/Project/VicRoads/Plates/home-content/best-sellers-deluxe-2.jpg',
    imageAlt: 'A car with a Deluxe style plate'
  }
];

const SCROLL_STEP = 300;

const BestSellers = ({ content }) => {
  const {
    title           = 'Best sellers',
    scrollBackText  = 'Scroll back',
    scrollForwardText = 'Scroll forward',
    viewMoreText    = 'View More',
    viewMoreLink    = '/browse-styles',
    items           = DEFAULT_PRODUCTS,
  } = content || {};

  const products = items.map((item) => ({
    name:     item.productName ?? item.name,
    href:     item.productLink ?? item.href,
    image:    item.imagePath   ?? item.image,
    imageAlt: item.imageAltText ?? item.imageAlt,
    width:    item.width,
    height:   item.height,
  }));

  const listRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const updateButtons = () => {
    const el = listRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 0);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 2);
  };

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateButtons, { passive: true });
    updateButtons();
    return () => el.removeEventListener('scroll', updateButtons);
  }, []);

  // Reset scroll to the first item whenever content changes (e.g. async CMS
  // content replacing the fallback DEFAULT_PRODUCTS on initial load).
  // Temporarily disable scroll-snap so the browser doesn't re-snap mid-reset.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.style.scrollSnapType = 'none';
    el.scrollLeft = 0;
    const rafId = window.requestAnimationFrame(() => {
      el.style.scrollSnapType = '';
      updateButtons();
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [content]);

  const scrollBack = () => {
    listRef.current?.scrollBy({ left: -SCROLL_STEP, behavior: 'smooth' });
  };

  const scrollNext = () => {
    listRef.current?.scrollBy({ left: SCROLL_STEP, behavior: 'smooth' });
  };

  return (
    <section className="home-products" tabIndex="-1">
      <div className="home-products__header">
        <h2 className="home-products__title">{title}</h2>
      </div>
      <div className="home-products__inner">
        <div className="home-products__buttons">
          <button
            className={`home-products__button home-products__button--back${atStart ? ' hidden' : ''}`}
            aria-controls="home-products-list"
            type="button"
            onClick={scrollBack}
            disabled={atStart}
          >
            <span className="visuallyhidden">{scrollBackText}</span>
          </button>
          <button
            className={`home-products__button home-products__button--next${atEnd ? ' hidden' : ''}`}
            aria-controls="home-products-list"
            type="button"
            onClick={scrollNext}
            disabled={atEnd}
          >
            <span className="visuallyhidden">{scrollForwardText}</span>
          </button>
        </div>
        <div className="home-products__container">
          <ul className="home-products__list" id="home-products-list" ref={listRef}>
            {products.map((product) => (
              <li key={product.name} className="home-products__item">
                <Link className="home-products__link" to={product.href}>
                  <img
                    className="home-products__image"
                    src={product.image}
                    width={product.width || 490}
                    height={product.height || 584}
                    loading="lazy"
                    alt={product.imageAlt}
                  />
                  <span className="home-products__name">{product.name}</span>
                </Link>
              </li>
            ))}
            <li className="home-products__item home-products__item--end">
              <Link className="home-products__view-more" to={viewMoreLink}>
                {viewMoreText}
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
};

BestSellers.propTypes = {
  content: PropTypes.shape({
    title:             PropTypes.string,
    scrollBackText:    PropTypes.string,
    scrollForwardText: PropTypes.string,
    viewMoreText:      PropTypes.string,
    viewMoreLink:      PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      productName:   PropTypes.string,
      productLink:   PropTypes.string,
      imagePath:     PropTypes.string,
      imageAltText:  PropTypes.string,
      width:         PropTypes.string,
      height:        PropTypes.string,
    })),
  }),
};

export default BestSellers;
